import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import WidgetErrorMessage from '../../WidgetErrorMessage';
import {
    getMetricSourcesForRequests,
    allMetricSourcesAuthorized,
    getMetricSourceErrors,
    displayMetricSourceError,
    areFirstGroupBysDifferent,
} from '../../utils/wrapperUtils';
import MultiChartComponent from './MultiChartComponent';

export function hasMetricSources(metricSources, requireAllSources = true) {
    if (requireAllSources) {
        return metricSources.every(
            (source) => source.metricSeries && source.metricSeries.length > 0
        );
    }

    return metricSources.find(
        (source) => source.metricSeries && source.metricSeries.length > 0
    );
}

/* eslint-disable complexity */
function getMetricSources({ metricRequests, metrics }) {
    if (
        metricRequests.length === 0 ||
        metricRequests.some((x) => x.groupBy.length === 0) ||
        metricRequests.some((x) => x.groupBy.length > 3) ||
        metricRequests.some((x) => x.metrics.length === 0)
    ) {
        return { errorKey: 'invalidMetricRequests' };
    }

    if (areFirstGroupBysDifferent(metricRequests)) {
        return { errorKey: 'metricRequestGroupBysDifferent' };
    }

    const metricSources = getMetricSourcesForRequests(metrics, metricRequests);
    if (metricSources.some((metricSource) => !metricSource)) {
        return { errorKey: 'metricSourceNotFound' };
    }

    if (!allMetricSourcesAuthorized(metricSources)) {
        return { errorKey: 'metricSourceUnauthorized' };
    }

    if (!hasMetricSources(metricSources)) {
        return { errorKey: 'missingData' };
    }

    const metricSourceErrors = getMetricSourceErrors(
        metricSources,
        metricRequests
    );
    if (metricSourceErrors.length) {
        return { metricSourceErrors };
    }

    return {
        metricSources,
        metricRequests,
    };
}
/* eslint-enable complexity */

function getGroupBy(metricRequests) {
    return metricRequests[0].groupBy[0];
}

export default function MultiChartWrapper(props) {
    const { metricSources, metricRequests, metricSourceErrors, errorKey } =
        getMetricSources(props);

    const metricSourcesGroupedSeries = metricSources.map((metricSource) =>
        _.groupBy(metricSource.metricSeries, getGroupBy(metricRequests))
    );

    function buildChartComponentForGroupValue(groupByValue) {
        const metrics = metricSources.map((metricSource, index) => ({
            ...metricSource,
            metricSeries: metricSourcesGroupedSeries[index][groupByValue],
        }));

        return {
            [getGroupBy(metricRequests)]: groupByValue,
            chart: (
                <MultiChartComponent
                    {...props}
                    key={groupByValue}
                    groupByValue={groupByValue}
                    metrics={metrics}
                    metricRequests={metricRequests}
                />
            ),
            sortOrder:
                props.configuration.sortOrder && metrics[0]?.metricSeries[0]
                    ? metrics[0].metricSeries[0][props.configuration.sortOrder]
                    : groupByValue,
        };
    }

    if (errorKey) {
        return (
            <WidgetErrorMessage>
                {props.dashboardLabels[errorKey]}
            </WidgetErrorMessage>
        );
    }

    if (metricSourceErrors) {
        return metricSourceErrors.map((error) => (
            <WidgetErrorMessage key={error.uniqueRequestString}>
                {displayMetricSourceError(error, props.dashboardLabels)}
            </WidgetErrorMessage>
        ));
    }

    const groupByValues = Object.keys(metricSourcesGroupedSeries[0]);

    const chartComponentsWithGroupValue = groupByValues.map(
        buildChartComponentForGroupValue
    );

    const charts = _(chartComponentsWithGroupValue)
        .orderBy(
            props.configuration.sortOrder
                ? 'sortOrder'
                : getGroupBy(metricRequests),
            'asc'
        )
        .map((x) => x.chart)
        .value();

    const gridStyles = {
        gridTemplateColumns: `repeat(${props.configuration.numberOfColumns}, 1fr)`,
    };

    return (
        <div className="multi-chart-container" style={gridStyles}>
            {charts}
        </div>
    );
}

MultiChartWrapper.propTypes = {
    configuration: PropTypes.instanceOf(Object).isRequired,
    dashboardLabels: PropTypes.instanceOf(Object).isRequired,
    openLinksInNewTab: PropTypes.bool,
};
